import { Box } from '@mui/material';
import IconFaceFrame from 'assets/icon_face_frame';
import { forwardRef, useEffect } from 'react';

interface VideoPreviewProps {
  isRecording: boolean;
  isPreview?: boolean;
  hasVideoAccess?: boolean;
  /**
   * Визуально скрывает превью, НЕ размонтируя <video>. Нужно, когда на экране
   * вместо камеры показывается аватар, но запись и детектор голоса должны
   * продолжать работать (они завязаны на videoRef.current / srcObject).
   */
  hidden?: boolean;
  /**
   * Компактный «zoom-пузырёк»: маленькое превью камеры в углу поверх аватара.
   */
  corner?: boolean;
}

// Стандартный «visually hidden»: элемент в DOM и продолжает играть, но не виден и не занимает место
const visuallyHiddenSx = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0
} as const;

/**
 * Вызывает play() на video элементе. setTimeout нужен для iOS/Capacitor —
 * вызов play() сразу после установки srcObject или при смене видимости может давать серый экран.
 */
const scheduleVideoPlay = (video: HTMLVideoElement) => {
  const id = setTimeout(() => {
    video.play().catch(() => {
      // Автовоспроизведение может блокироваться — это ок для muted потоков
    });
  }, 50);
  return () => clearTimeout(id);
};

/**
 * Компонент для отображения превью видео с камеры
 */
const VideoPreview = forwardRef<HTMLVideoElement, VideoPreviewProps>(
  ({ isRecording, isPreview = true, hasVideoAccess = false, hidden = false, corner = false }, ref) => {
    // Видео показывается только когда есть доступ к видео И (isPreview или isRecording)
    const shouldShowVideo = hasVideoAccess && (isPreview || isRecording);

    // iOS/Capacitor: явный play() после отображения и при появлении srcObject
    useEffect(() => {
      const videoEl =
        ref && typeof ref === 'object' && 'current' in ref ? ref.current : null;
      if (!videoEl || !shouldShowVideo || !videoEl.srcObject) return;
      return scheduleVideoPlay(videoEl);
    }, [shouldShowVideo, ref]);

    // iOS: при возврате на вкладку/страницу видео может "зависнуть" — перезапускаем
    useEffect(() => {
      const videoEl =
        ref && typeof ref === 'object' && 'current' in ref ? ref.current : null;
      if (!videoEl || !shouldShowVideo || !videoEl.srcObject) return;

      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          scheduleVideoPlay(videoEl);
        }
      };
      document.addEventListener('visibilitychange', handleVisibilityChange);
      return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [shouldShowVideo, ref]);

    const cornerSx = {
      minWidth: { xs: 110, sm: 95 },
      maxWidth: { xs: 110, sm: 95 },
      aspectRatio: '3 / 4',
      bgcolor: 'grey.900',
      borderRadius: 2,
      border: '2px solid #fff',
      boxShadow: 6,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
      position: 'relative'
    };

    const fullSx = {
      mt: 4,
      width: '100%',
      maxWidth: { xs: '100%', sm: 510 },
      aspectRatio: { xs: '1 / 1', sm: '16 / 9' },
      maxHeight: { xs: 'calc(var(--vh, 1vh) * 40)', sm: 'calc(var(--vh, 1vh) * 50)' },
      bgcolor: 'grey.200',
      borderRadius: 2,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
      position: 'relative'
    };

    return (
      <Box sx={hidden ? visuallyHiddenSx : corner ? cornerSx : fullSx}>
        <video
          ref={ref}
          autoPlay
          playsInline
          muted
          style={{
            width: '100%',
            objectFit: 'cover',
            height: '100%',
            visibility: shouldShowVideo ? 'visible' : 'hidden',
            position: shouldShowVideo ? 'relative' : 'absolute',
            transform: 'rotateY(180deg)'
          }}
        />
        {/* Рамка показывается только при isPreview (и не в компактном угловом режиме) */}
        {isPreview && !corner && (
          <IconFaceFrame sx={{ width: '100%', height: '100%', position: 'absolute' }} />
        )}
      </Box>
    );
  }
);

VideoPreview.displayName = 'VideoPreview';

export default VideoPreview;
